/**
 * Modelo do editor visual de etiquetas.
 *
 * Geometria em MILÍMETROS (independente de DPI). O gerador converte para dots
 * na hora de emitir o ZPL, usando o dpmm da etiqueta.
 * Exceções (inerentemente em dots): módulo do código de barras, espessura de
 * borda, magnificação do QR e o bitmap da imagem.
 */

export type TipoElemento =
  | "texto"
  | "barras"
  | "qrcode"
  | "imagem"
  | "caixa"
  | "linha"
  | "bruto";

export type Rotacao = 0 | 90 | 180 | 270;

export type FonteZpl = "0" | "A" | "B" | "D" | "E" | "F" | "G" | "H";

export type Simbologia =
  | "CODE128"
  | "CODE39"
  | "CODE93"
  | "EAN13"
  | "EAN8"
  | "UPCA"
  | "ITF";

export type ElementoComum = {
  id: string;
  tipo: TipoElemento;
  nome?: string;
  /** canto superior esquerdo, em mm */
  x: number;
  y: number;
  rotacao: Rotacao;
  bloqueado?: boolean;
  oculto?: boolean;
};

export type ElementoTexto = ElementoComum & {
  tipo: "texto";
  texto: string;
  fonte: FonteZpl;
  /** altura do caractere, em mm */
  alturaFonte: number;
  /** largura do caractere, em mm */
  larguraFonte: number;
  /** ^FR — imprime invertido (branco sobre preto) */
  inverso?: boolean;
  /** > 0 ativa ^FB (bloco de texto com quebra automática), em mm */
  larguraBloco?: number;
  maxLinhas?: number;
  alinhamento?: "L" | "C" | "R" | "J";
};

export type ElementoBarras = ElementoComum & {
  tipo: "barras";
  dados: string;
  simbologia: Simbologia;
  /** altura das barras, em mm */
  altura: number;
  /** largura do módulo estreito, em dots (1-10) */
  modulo: number;
  /** razão largo/estreito (2.0-3.0) */
  razao: number;
  mostrarTexto: boolean;
  textoAcima: boolean;
};

export type ElementoQrcode = ElementoComum & {
  tipo: "qrcode";
  dados: string;
  /** magnificação 1-10 (dots por módulo) */
  magnificacao: number;
  correcao: "L" | "M" | "Q" | "H";
};

export type ElementoImagem = ElementoComum & {
  tipo: "imagem";
  /** bitmap monocromático já pronto pro ^GFA */
  larguraDots: number;
  alturaDots: number;
  bytesPorLinha: number;
  /** dados hex maiúsculos, sem compressão */
  hex: string;
  arquivo?: string;
};

export type ElementoCaixa = ElementoComum & {
  tipo: "caixa" | "linha";
  /** em mm */
  largura: number;
  altura: number;
  /** espessura da borda, em dots */
  espessura: number;
  /** arredondamento 0-8 */
  arredondamento: number;
  /** preenche todo o retângulo (espessura = altura) */
  preenchido?: boolean;
  /** desenha em branco (útil sobre área preta) */
  inverso?: boolean;
};

/** Trecho de ZPL que o parser não soube interpretar — preservado na íntegra. */
export type ElementoBruto = ElementoComum & {
  tipo: "bruto";
  zpl: string;
};

export type EtiquetaElemento =
  | ElementoTexto
  | ElementoBarras
  | ElementoQrcode
  | ElementoImagem
  | ElementoCaixa
  | ElementoBruto;

export type EtiquetaDesign = {
  versao: 1;
  elementos: EtiquetaElemento[];
  /** comando de codificação de caracteres (padrão ^CI28 = UTF-8) */
  codificacao?: string;
  /** blocos ~DG/~DY emitidos ANTES do ^XA (download de logo na impressora) */
  downloads?: string;
  /** comandos extras emitidos logo após o ^XA (ex.: ^MD, ^PR, ^LH) */
  preambulo?: string;
  /** comandos extras emitidos antes do ^XZ (ex.: ^PQ) */
  posfixo?: string;
};

export const criarDesignVazio = (): EtiquetaDesign => ({
  versao: 1,
  elementos: [],
});
